import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import BoxIconFilled from "@redotech/redo-icons/box_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageFilledIcon from "@redotech/redo-icons/package_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import {
  BATCH_HISTORY_ICON,
  BATCH_HISTORY_ICON_FILLED,
  CLAIMS_ICON,
  CLAIMS_ICON_FILLED,
  EOD_MANIFEST_ICON,
  EOD_MANIFEST_ICON_FILLED,
  OrderManagementSuiteStatusesContext,
  PERSONALIZED_INSERTS_ICON,
  PERSONALIZED_INSERTS_ICON_FILLED,
  PICK_SESSIONS_ICON,
  PICK_SESSIONS_ICON_FILLED,
  PRINT_JOBS_ICON,
  PRINT_JOBS_ICON_FILLED,
  RATE_CALCULATOR_ICON,
  RATE_CALCULATOR_ICON_FILLED,
  SCAN_ICON,
  SCAN_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { DashboardItem } from "../orders/dashboard";
import { FulfillmentItem } from "../orders/fulfillment";
import { PickupsItem } from "../orders/pickups";
import { WarehouseItem } from "../orders/warehouse";
import { ConsolidationsNavItem } from "./consolidations-nav-item";
import { ShippingAnalyticsItem } from "./shipping-analytics";

export function useShippingNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const pathRoot = routes.shippingBaseUrl;
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const { t } = useTranslation();

  const canViewShippingSection = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  if (!canViewShippingSection) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "shipping",
    hasNotification: false,
    UnfilledIcon: PackageIcon,
    FilledIcon: PackageFilledIcon,
    type: "link",
    pathRoot,
    href: routes.shippingDefaultRoute,
    friendlyName: t("Shipping", {
      context:
        "primary navigation section for shipping and fulfillment operations",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    omsEnabled
      ? {
          id: "shipping",
          name: t("Shipping & fulfillments", {
            context:
              "navigation section for shipping and order fulfillment operations",
          }),
          NavContent: ShippingSecondaryNavigation,
        }
      : undefined;

  return { primaryTab, secondaryNavigationData, isActiveProduct: omsEnabled };
}

const ShippingSecondaryNavigation = memo(
  function ShippingSecondaryNavigation() {
    const { t } = useTranslation();
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: t("Shipping & fulfillments", {
        context:
          "navigation section for shipping and order fulfillment operations",
      }),
    });
    const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

    return (
      <TreeContext.Provider value={nodeId}>
        <DashboardItem />
        <ShippingAnalyticsItem />
        <BatchHistoryItem />
        <CarrierClaimsItem />
        {omsEnabled && <PickupsItem />}
        <ConsolidationsNavItem />
        <EodManifestItem />
        <FulfillmentItem />
        <OverboxLabelsItem />
        <PersonalizedInsertsItem />
        <PickSessionsItem />
        <PrintJobsItem />
        <RateCalculatorItem />
        <ScanItem />
        <WarehouseItem />
      </TreeContext.Provider>
    );
  },
);

const BatchHistoryItem = memo(function BatchHistoryItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("batch-history"),
      }}
      FilledIcon={BATCH_HISTORY_ICON_FILLED}
      friendlyName={t("Batch history", {
        context: "shipping and fulfillment operations navigation label",
      })}
      hidden={!omsEnabled || !canViewShippingTabs}
      id="batch-history"
      UnfilledIcon={BATCH_HISTORY_ICON}
    />
  );
});

const CarrierClaimsItem = memo(function CarrierClaimsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const isReturnEnabled = team?.settings.returns?.enabled;
  const canManageCarrierClaims = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.shippingCarrierClaimsHref }}
      FilledIcon={CLAIMS_ICON_FILLED}
      friendlyName={t("Carrier claims", {
        context: "shipping and fulfillment operations navigation label",
      })}
      hidden={!((omsEnabled || isReturnEnabled) && canManageCarrierClaims)}
      id="carrier-claims"
      UnfilledIcon={CLAIMS_ICON}
    />
  );
});

const EodManifestItem = memo(function EodManifestItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("eod-manifest"),
      }}
      FilledIcon={EOD_MANIFEST_ICON_FILLED}
      friendlyName={t("EOD manifest", {
        context: "shipping navigation label for end-of-day carrier manifests",
      })}
      hidden={!omsEnabled || !canViewShippingTabs}
      id="eod-manifest"
      UnfilledIcon={EOD_MANIFEST_ICON}
    />
  );
});

const OverboxLabelsItem = memo(function OverboxLabelsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  const overboxLabelsEnabled =
    !!team?.settings.outboundLabels?.useApcCarrierAccount ||
    !!team?.settings.outboundLabels?.useEpostGlobalCarrierAccount;

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("overbox-labels"),
      }}
      FilledIcon={BoxIconFilled}
      friendlyName={t("Overbox labels", {
        context:
          "shipping navigation label for labels attached to outer shipping boxes",
      })}
      hidden={!omsEnabled || !canViewShippingTabs || !overboxLabelsEnabled}
      id="overbox-labels"
      UnfilledIcon={BoxIcon}
    />
  );
});

const PersonalizedInsertsItem = memo(function PersonalizedInsertsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  // Monetization off is the true dead-end; with it on, the page shows the
  // terms flow, so keep the item reachable.
  const personalizedInsertsEnabled =
    !!team?.settings.outboundLabels?.personalizedSlipsMonetization?.enabled;

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("personalized-inserts"),
      }}
      FilledIcon={PERSONALIZED_INSERTS_ICON_FILLED}
      friendlyName={t("Personalized Inserts", {
        context:
          "shipping navigation label for personalized materials inserted into packages",
      })}
      hidden={
        !omsEnabled || !canViewShippingTabs || !personalizedInsertsEnabled
      }
      id="personalized-inserts"
      UnfilledIcon={PERSONALIZED_INSERTS_ICON}
    />
  );
});

const PickSessionsItem = memo(function PickSessionsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled, wmsEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const canManagePickSessions = useHasPermission(
    OrderManagementPermissions.MANAGE_PICK_SESSIONS,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.pickSessionTableHref }}
      FilledIcon={PICK_SESSIONS_ICON_FILLED}
      friendlyName={t("Pick sessions", {
        context:
          "warehouse navigation label for worker sessions picking ordered items from storage shelf or bin locations during order fulfillment, not product sampling or withdrawals",
      })}
      hidden={!omsEnabled || !wmsEnabled || !canManagePickSessions}
      id="pick-sessions"
      UnfilledIcon={PICK_SESSIONS_ICON}
    />
  );
});

const PrintJobsItem = memo(function PrintJobsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("print-jobs"),
      }}
      FilledIcon={PRINT_JOBS_ICON_FILLED}
      friendlyName={t("Print jobs", {
        context: "shipping and fulfillment operations navigation label",
      })}
      hidden={!omsEnabled || !canViewShippingTabs}
      id="print-jobs"
      UnfilledIcon={PRINT_JOBS_ICON}
    />
  );
});

const RateCalculatorItem = memo(function RateCalculatorItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("rate-calculator"),
      }}
      FilledIcon={RATE_CALCULATOR_ICON_FILLED}
      friendlyName={t("Rate calculator", {
        context: "shipping and fulfillment operations navigation label",
      })}
      hidden={!omsEnabled || !canViewShippingTabs}
      id="rate-calculator"
      UnfilledIcon={RATE_CALCULATOR_ICON}
    />
  );
});

const ScanItem = memo(function ScanItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewScan = useHasPermission(
    OrderManagementPermissions.SCAN_AND_VERIFY,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getOrdersOutboundLabelsSubRoute("scan"),
      }}
      FilledIcon={SCAN_ICON_FILLED}
      friendlyName={t("Scan", {
        context: "shipping navigation action for scanning packages",
      })}
      hidden={!omsEnabled || !canViewScan}
      id="scan"
      UnfilledIcon={SCAN_ICON}
    />
  );
});
